import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";
import { Clock3, Eye, Loader2, MailPlus, UserRound } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import type { AdminTableColumn } from "@/components/ui/admin-table";
import { AdminTable } from "@/components/ui/admin-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/page-header";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UserStatusBadge } from "@/components/ui/user-status-badge";
import { useAuth } from "@/features/auth/use-auth";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { adminApi } from "@/lib/auth-api";
import type { UserStatus } from "@/lib/auth-types";
import { ensureElementVisible } from "@/lib/route-guards";

export const Route = createFileRoute("/_auth/konfiguracja/podglad-uzytkownika")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.system.podglad-uzytkownika",
			location.pathname,
		);
	},
	component: PodgladUzytkownikaPage,
});

type ImpersonationTarget = Awaited<
	ReturnType<typeof adminApi.getImpersonationTargets>
>["data"][number];

function PodgladUzytkownikaPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { startImpersonation, isImpersonating } = useAuth();
	const [search, setSearch] = useState("");
	const [startingId, setStartingId] = useState<number | null>(null);
	const [invitingId, setInvitingId] = useState<number | null>(null);
	// Stan trzyma encje, nie boolean — tytul i tresc dialogu biora dane z wybranego wiersza.
	const [confirmInvite, setConfirmInvite] = useState<ImpersonationTarget | null>(null);
	const [error, setError] = useState<string | null>(null);
	const isBelowMd = useIsBelowMd();

	const { data, isLoading } = useQuery({
		queryKey: ["impersonation-targets"],
		queryFn: () => adminApi.getImpersonationTargets(),
	});

	const targets = (data?.data ?? []).filter((t) => {
		const q = search.trim().toLowerCase();
		if (!q) return true;
		return (
			t.email.toLowerCase().includes(q) ||
			t.name.toLowerCase().includes(q) ||
			t.pharmacies.some((p) => p.toLowerCase().includes(q))
		);
	});

	// Potwierdzenie idzie przez ConfirmDialog, a NIE przez natywny dialog
	// przegladarki: przy nieaktywnej karcie Chrome go wycisza i zwraca false,
	// przez co klikniecie „wyslij ponownie" konczylo sie cichym brakiem reakcji
	// — bez maila i bez zadnego komunikatu dla kierownika.
	const wyslijZaproszenie = async (target: ImpersonationTarget) => {
		setError(null);
		setInvitingId(target.userId);
		try {
			await adminApi.inviteUser(target.userId);
			toast.success(`Zaproszenie wysłane na ${target.email}`);
			queryClient.invalidateQueries({ queryKey: ["impersonation-targets"] });
		} catch (e) {
			setError(e instanceof Error ? e.message : "Nie udało się wysłać zaproszenia");
		} finally {
			setInvitingId(null);
			setConfirmInvite(null);
		}
	};

	const handleStart = async (userId: number) => {
		// Backend odmawia podgladu z podgladu (403, FARAMSTER-BACKEND-75): operator
		// podgladajacy kierownika widzi jego strone i jego przycisk.
		if (isImpersonating) return;
		setError(null);
		setStartingId(userId);
		try {
			await startImpersonation(userId);
			navigate({ to: "/dashboard" });
		} catch (e) {
			setError(e instanceof Error ? e.message : "Nie udało się uruchomić podglądu");
		} finally {
			setStartingId(null);
		}
	};

	// Telefon: kolumna „Użytkownik” (awatar + dwie linie) rozbita na tytul i podtytul
	// wiersza listy; na desktopie zostaje oryginalna kolumna.
	const userColumns: AdminTableColumn<ImpersonationTarget>[] = isBelowMd
		? [
				{
					key: "name",
					header: "Użytkownik",
					mobileRole: "title",
					render: (target) => target.name,
				},
				{
					key: "email",
					header: "Email",
					mobileRole: "subtitle",
					render: (target) => target.email,
				},
			]
		: [
				{
					key: "user",
					header: "Użytkownik",
					width: "22%",
					render: (target) => (
						<div className="flex min-w-0 items-center gap-3">
							<div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
								<UserRound className="size-4.5" aria-hidden />
							</div>
							<div className="min-w-0">
								<p className="truncate font-semibold text-foreground">{target.name}</p>
								<p className="truncate text-muted-foreground">{target.email}</p>
							</div>
						</div>
					),
				},
			];

	const columns: AdminTableColumn<ImpersonationTarget>[] = [
		...userColumns,
		{
			key: "pharmacies",
			header: "Apteki",
			width: "24%",
			hiddenOnMobile: true,
			render: (target) => (
				<p className="truncate text-muted-foreground" title={target.pharmacies.join(", ")}>
					{target.pharmacies.length > 0 ? (
						<>
							{target.pharmacies.slice(0, 4).join(" · ")}
							{target.pharmacies.length > 4 && ` (+${target.pharmacies.length - 4})`}
						</>
					) : (
						"—"
					)}
				</p>
			),
		},
		{
			key: "lastLoginAt",
			header: "Ostatnie logowanie",
			width: "14%",
			mobileRole: "detail",
			render: (target) => (
				<div className="flex items-center gap-2">
					<Clock3 className="size-4 shrink-0 text-primary" aria-hidden />
					{target.lastLoginAt ? (
						<div className="min-w-0">
							<p className="whitespace-nowrap font-medium text-foreground">
								{new Date(target.lastLoginAt).toLocaleString("pl-PL", {
									dateStyle: "short",
									timeStyle: "short",
								})}
							</p>
							<p className="whitespace-nowrap text-[11px] text-muted-foreground">
								{formatDistanceToNow(new Date(target.lastLoginAt), {
									addSuffix: true,
									locale: pl,
								})}
							</p>
						</div>
					) : (
						<span className="font-medium text-muted-foreground">Nigdy</span>
					)}
				</div>
			),
		},
		{
			key: "roles",
			header: "Role",
			width: "16%",
			hiddenOnMobile: true,
			render: (target) => (
				<div className="flex max-h-10 flex-wrap gap-1 overflow-hidden">
					{target.roles.map((role) => (
						<Badge key={role} variant="outline" className="max-w-full truncate">
							{role}
						</Badge>
					))}
				</div>
			),
		},
		{
			key: "status",
			header: "Status",
			width: "10%",
			hiddenOnMobile: true,
			mobileRole: "measure",
			render: (target) => (
				<UserStatusBadge
					status={
						(target.status as UserStatus | null) ?? (target.isActive ? "active" : "deactivated")
					}
				/>
			),
		},
		{
			key: "actions",
			header: "Akcje",
			// 14% + padding komorki (2x16px) mieszcza "Podglad" i ikone zaproszenia;
			// przy 10% ikona wychodzila na krawedz tabeli.
			width: "14%",
			render: (target) => (
				<div className="flex items-center gap-1 whitespace-nowrap">
					<Button
						size="sm"
						variant="outline"
						disabled={startingId !== null || isImpersonating}
						onClick={() => handleStart(target.userId)}
					>
						{startingId === target.userId ? (
							<Loader2 className="size-4 animate-spin" />
						) : (
							<Eye className="size-4" />
						)}
						Podgląd
					</Button>
					{/* Zaproszenie ma sens tylko dla konta, ktore jeszcze nie ustawilo hasla. */}
					{(target.status === "draft" || target.status === "invited") && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									size="icon"
									variant="outline"
									aria-label="Wyślij zaproszenie ponownie"
									disabled={invitingId !== null}
									onClick={() => setConfirmInvite(target)}
								>
									{invitingId === target.userId ? (
										<Loader2 className="size-4 animate-spin" />
									) : (
										<MailPlus className="size-4" />
									)}
								</Button>
							</TooltipTrigger>
							<TooltipContent>Wyślij zaproszenie do Farmastera ponownie</TooltipContent>
						</Tooltip>
					)}
				</div>
			),
		},
	];

	return (
		<div className="p-6 space-y-4 max-md:space-y-3 max-md:p-3">
			{isBelowMd && (
				<PageHeader
					title="Podgląd użytkownika"
					description="Zobacz system oczami wybranego użytkownika — widzisz dokładnie to, co on, przez czas trwania sesji podglądu. Lista obejmuje tylko konta z aptek w Twoim zakresie."
				/>
			)}
			<Card className="max-md:gap-0 max-md:border-0 max-md:bg-transparent max-md:py-0 max-md:shadow-none">
				<CardHeader className="max-md:hidden">
					<CardTitle>Podgląd użytkownika</CardTitle>
					<CardDescription>
						Zobacz system oczami wybranego użytkownika — widzisz dokładnie to, co on, przez czas
						trwania sesji podglądu. Lista obejmuje tylko konta z aptek w Twoim zakresie.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4 max-md:space-y-3 max-md:px-0">
					<Input
						placeholder="Szukaj po email, nazwisku lub aptece..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="max-w-sm max-md:max-w-none"
					/>
					{/* Tekst, nie tooltip: na wylaczonym przycisku tooltip sie nie pokazuje. */}
					{isImpersonating && (
						<p className="text-sm text-muted-foreground">
							Zakończ aktywny podgląd, zanim rozpoczniesz nowy.
						</p>
					)}
					{error && <p className="text-sm text-[var(--status-error-text)]">{error}</p>}
					{isLoading ? (
						<div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
							<Loader2 className="w-4 h-4 animate-spin" /> Ładowanie...
						</div>
					) : (
						<AdminTable
							columns={columns}
							data={targets}
							keyField="userId"
							rowHeight={64}
							totalCount={targets.length}
							emptyMessage="Brak użytkowników dostępnych do podglądu."
							tableId="impersonation-targets"
						/>
					)}
				</CardContent>
			</Card>

			<ConfirmDialog
				open={confirmInvite != null}
				onOpenChange={(o) => !o && setConfirmInvite(null)}
				title="Wysłać zaproszenie ponownie?"
				description={
					confirmInvite
						? `Zaproszenie do Farmastera zostanie wysłane na ${confirmInvite.email}.`
						: undefined
				}
				confirmLabel="Wyślij"
				isLoading={invitingId !== null}
				onConfirm={() => confirmInvite && wyslijZaproszenie(confirmInvite)}
			/>
		</div>
	);
}
